import { clsx, type ClassValue } from "clsx";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Recursive function to extract required fields
export const getRequiredFields = (schema: any, prefix = ""): string[] => {
  return Object.entries(schema.shape)?.flatMap(([key, field]) => {
    const fieldName = prefix ? `${prefix}.${key}` : key;

    if (field instanceof z.ZodObject) {
      return getRequiredFields(field, fieldName); // Recurse for nested objects
    } else if (field instanceof z.ZodArray) {
      return getRequiredFields(field._def.type, fieldName); // Check array elements
    } else if (field instanceof z.ZodString && !field.isOptional()) {
      return fieldName; // Add required field
    } else if (field instanceof z.ZodNumber && !field.isOptional()) {
      return fieldName; // Add required field
    } else if (field instanceof z.ZodDate && !field.isOptional()) {
      return fieldName; // Add required field
    }

    return [];
  });
};

export const removeEmptyFields = (data: any) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
};

export const setEmptyFieldsToNull = (data: any) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == "") {
      data[key] = null;
    }
  });
  return data;
};

export const sortByDate = (data: any, key: string) => {
  const a = new Date(data[key]).getTime();
  const b = new Date(data[key]).getTime();
  return a < b ? -1 : a > b ? 1 : 0;
};

export const formatAmount = (
  amount: number | string,
  maximumFractionDigits: number = 0,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "$0";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: maximumFractionDigits,
  }).format(numericAmount);
};

export const yearOptions = (length: number) => {
  return new Array(length)
    .fill(0)
    .map((_, index: number) => ({
      label: (moment().year() - 2 + index).toString(),
      value: (moment().year() - 2 + index).toString(),
    }))
    .reverse();
};

export const monthOptions = [
  { label: "All", value: "all" },
  ...moment.months().map((month, index) => ({
    label: month,
    value: (index + 1).toString().padStart(2, "0"),
  })),
];

export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTwoDigits = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

export const formatCurrencyShort = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value}`;
};

export const calculatePercentageChange = (
  current: number,
  previous: number
): { value: string; progress: string } => {
  if (previous == 0) return { value: "0", progress: "" };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";

  return {
    value: change ? `${sign}${(change || 0).toFixed(2)}%` : "0",
    progress: change ? (change >= 0 ? "up" : "down") : "",
  };
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
